import {
  SESSION_STORAGE_LOCATION_ERROR_SHOWED,
  SESSION_STORAGE_LOCATION_MESSAGE,
  userSelectedConstants,
} from '../constants';
import {
  getEnv,
  getUserSelectedInfo,
  isServerless,
  setSessionStorageIfNotExist,
} from 'helpers';
import { LocationMessageType, TenantEnv } from 'types';
import { BrowserEnv } from 'libs';

// linter replaces "interface" with "type"
// which causes compile error
// so disable lint for next line
// eslint-disable-next-line
interface ClientTenantEnvImpl extends TenantEnv {}
class ClientTenantEnvImpl {
  constructor(config: TenantEnv) {
    if (getEnv('EPIC_ECOM_284') === 'enabled') {
      // From ecom 284, shop proxy no longer caches responses by terminal or by location,
      // so the terminal and location returned by server are unchanged for every requests.
      // This statement handles re-assigning these configs based on the chosen one of the users.
      // It also sets some variables to storages for showing some necessary popups.
      if (isServerless()) {
        if (!config.locations?.length) {
          // Show the error popup when locations aren't found
          setSessionStorageIfNotExist(
            SESSION_STORAGE_LOCATION_MESSAGE,
            LocationMessageType.Error,
          );

          // This variable mark when the error popup showed.
          // Clear it to defense the case of users reload the page.
          sessionStorage.removeItem(SESSION_STORAGE_LOCATION_ERROR_SHOWED);
        } else if (
          config.locations.length === 1 &&
          config.locations[0].locationCode === '0'
        ) {
          // With the above condition, don't show any messages.
          setSessionStorageIfNotExist(
            SESSION_STORAGE_LOCATION_MESSAGE,
            LocationMessageType.None,
          );
        } else {
          // Use the location that chose by users and stored in localStorage.
          const locationCodeFromLS = localStorage.getItem(
            userSelectedConstants.LOCATION,
          );

          if (!locationCodeFromLS) {
            // If the location in localStorage is not exist, use the default one returned by the server.
            // Show the hint for this case
            setSessionStorageIfNotExist(
              SESSION_STORAGE_LOCATION_MESSAGE,
              LocationMessageType.Hint,
            );

            // Set the default location to localStorage
            localStorage.setItem(
              userSelectedConstants.LOCATION,
              config.location,
            );
          } else {
            // If the user has chosen a location that stored in localStorage, verify it in the list returned by server.
            const locationOptionFromConfig = config.locations.find(
              ({ locationCode }) => locationCode === locationCodeFromLS,
            );

            if (!locationOptionFromConfig) {
              // If the chosen of user is not valid, handle it as the case of the location not being chosen
              setSessionStorageIfNotExist(
                SESSION_STORAGE_LOCATION_MESSAGE,
                LocationMessageType.Hint,
              );
              localStorage.setItem(
                userSelectedConstants.LOCATION,
                config.location,
              );
            } else {
              // If the chosen of user is valid, don't show any messages also.
              // Re-assign the configs by the chosen one of the user.
              setSessionStorageIfNotExist(
                SESSION_STORAGE_LOCATION_MESSAGE,
                LocationMessageType.None,
              );

              config.location = locationOptionFromConfig.locationCode;

              // Ecom 284 do not distinguish terminal between mobile and desktop,
              // so we still need to hard code the mobile one in workload config.
              // This step will get the right terminal based on device types.
              if (!config.isMobile) {
                config.terminalId = locationOptionFromConfig.terminalId;
                config.terminal = locationOptionFromConfig.terminalCode;
              } else {
                const terminalForMobile =
                  config.mobileTerminalIdMapping?.[
                    locationOptionFromConfig.terminalId.toString()
                  ];

                config.terminalId =
                  terminalForMobile?.terminalId ||
                  locationOptionFromConfig.terminalId;
                config.terminal =
                  terminalForMobile?.terminalCode ||
                  locationOptionFromConfig.terminalCode;
              }
            }
          }
        }
      }
    } else {
      // Get terminal from cookie in case tenant has terminal by region (ex: karavan, umbala)
      // This help avoid disk cache from browser when change location
      // More detail: https://jira.teko.vn/browse/UX4BUYER-2566
      if (Array.isArray(config.originalTerminal)) {
        config.terminal =
          getUserSelectedInfo(
            userSelectedConstants.TERMINAL,
            config.isMobile,
          ) || config.terminal;

        config.terminalId =
          parseInt(
            getUserSelectedInfo(
              userSelectedConstants.TERMINAL_ID,
              config.isMobile,
            ),
          ) || config.terminalId;

        config.location =
          getUserSelectedInfo(
            userSelectedConstants.LOCATION,
            config.isMobile,
          ) || config.location;
      }
    }

    Object.assign(this, config);

    // assign the object to window
    // this a trick to persist the
    // config object for client-side only pages
    window.__tenantConfig = config;
  }

  getProcessEnv(env: string): string {
    return window?.env?.[env as keyof BrowserEnv];
  }
}

export { ClientTenantEnvImpl };
